import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fade, Flex, HStack, Text, useToast } from '@chakra-ui/react';
import {
  HostTrackingEventName,
  useHostTrackingContext,
} from '@/features/hostTracking/HostTrackingProvider';
import { captureSentry } from '@/utils/helpers/sentryHelper';
import { SlideItemType, SlideType } from '@/utils/types/enums';
import isSlideQuestionSlide from '@/utils/types/type-validation/isSlideQuestionSlide';
import isSlideQusestionMultipleAnswerSlide from '@/utils/types/type-validation/isSlideQusestionMultipleAnswerSlide';
import { Asset } from '@/utils/types/zod/assetSchema';
import {
  QuestionSlideItemType,
  QuestionSlideType,
} from '@/utils/types/zod/slideTypes/questionSlideSchema';
import ItemContainer from '../ItemContainer';
import ItemListContainer from '../ItemListContainer';
import InfoItem from '../items/InfoItem';
import ConfirmButton from './components/ConfirmButton';
import SelectableAnswerItem from './components/SelectableAnswerItem';
import TextAnswerItem from './components/TextAnswerItem';
import { useQuestionContext } from './context/QuestionContext';

type Props = {
  isHost: boolean;
  slide: QuestionSlideType;
  isPreview: boolean;
  assetList: Asset[];
  onShowAssets: (assetRefs: string[]) => void;
  currentHostAssetID: string | null | undefined;
};

export const answerGlowDuration = 2000;

function QuestionSlide({
  slide,
  assetList,
  isPreview,
  isHost,
  onShowAssets,
  currentHostAssetID,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    isAnswerConfirmed,
    currentSelectedAnswers,
    onSelectableAnswerClick,
    onTextAnswerChange,
    onConfirmAnswer,
    glowingVoteItem,
    hasAtleastSelectedOneAnswer,
    slideHasAtLeastOneSelectableAnswer,
  } = useQuestionContext();
  const [hasBeenNotifiedOfAmtAnswersLeft, setHasBeenNotifiedOfAmtAnswersLeft] =
    useState<boolean>(false);
  let isConfirmButtonEnabled = hasAtleastSelectedOneAnswer && !isAnswerConfirmed;
  const { logHostAction } = useHostTrackingContext();

  //min nbr of choices are selected feature
  if (isConfirmButtonEnabled) {
    if (
      (slide.type === SlideType.QUESTION_MULTIPLE &&
        currentSelectedAnswers.length < slide.minChoices) ||
      (slide.type === SlideType.QUESTION_MULTIPLE_ORDERED &&
        currentSelectedAnswers.length < slide.correctOrderIds.length)
    ) {
      isConfirmButtonEnabled = false;
    }
  }

  const showSelectToast = () => {
    toast({
      status: 'warning',
      title: t('warning.selectAnswer'),

      position: 'top',
    });
  };
  const showConfirmToast = () => {
    toast({
      status: 'warning',
      title: t('warning.confirmChoice'),
      position: 'top',
    });
  };

  // shows answers and selection toast
  useEffect(() => {
    let timer: number;
    if (!isHost) {
      timer = window.setTimeout(() => {
        if (slideHasAtLeastOneSelectableAnswer && !hasAtleastSelectedOneAnswer) showSelectToast();
        else if (slideHasAtLeastOneSelectableAnswer && !isAnswerConfirmed) showConfirmToast();
      }, 10000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [slideHasAtLeastOneSelectableAnswer, hasAtleastSelectedOneAnswer, isAnswerConfirmed]);

  useEffect(() => {
    if (
      currentSelectedAnswers.length === 1 &&
      !isAnswerConfirmed &&
      slide.type === SlideType.QUESTION_MULTIPLE &&
      !hasBeenNotifiedOfAmtAnswersLeft &&
      slide.maxChoices - 1 > 0 //NOTE: Should always be true but still
    ) {
      toast({
        status: 'info',
        title: t('choseAnswerBannder.multipleAnswers.notification.title', {
          amt: slide.maxChoices - 1,
        }),
        position: 'top',
      });
      setHasBeenNotifiedOfAmtAnswersLeft(true);
    }
  }, [currentSelectedAnswers, isAnswerConfirmed, hasBeenNotifiedOfAmtAnswersLeft, slide]);

  return (
    <>
      {isSlideQuestionSlide(slide) &&
        !isPreview &&
        !isHost &&
        slideHasAtLeastOneSelectableAnswer && (
          <Fade in={true} key={`notification${isAnswerConfirmed}`} delay={0.5}>
            <Flex
              position="absolute"
              w="full"
              justifyContent="center"
              userSelect="none"
              pointerEvents="none"
              zIndex={2}
              data-test={isAnswerConfirmed ? 'banner-answerSubmitted' : 'banner-choseAnswer'}
            >
              <HStack
                w="fit-content"
                color={isAnswerConfirmed ? 'livelinx.green50' : 'livelinx.text'}
                bgColor={isAnswerConfirmed ? 'livelinx.green200' : 'livelinx.blue50'}
                px={4}
                py={1}
                borderRadius="md"
              >
                <Text>
                  ⭐️ {isAnswerConfirmed && t('home.slide.banner.answerSubmitted.title')}
                  {!isAnswerConfirmed &&
                    !isSlideQusestionMultipleAnswerSlide(slide) &&
                    t('home.slide.banner.choseAnswerBanner.singleAnswer.title')}
                  {!isAnswerConfirmed &&
                    isSlideQusestionMultipleAnswerSlide(slide) &&
                    t('home.slide.banner.choseAnswerBanner.multipleAnswers.title', {
                      amt:
                        slide.type === SlideType.QUESTION_MULTIPLE
                          ? slide.maxChoices
                          : slide.correctOrderIds.length,
                    })}
                </Text>
              </HStack>
            </Flex>
          </Fade>
        )}
      {isSlideQusestionMultipleAnswerSlide(slide) &&
        isHost &&
        slideHasAtLeastOneSelectableAnswer && (
          <Fade in={true} key="host-notification" delay={0.5}>
            <Flex
              position="absolute"
              w="full"
              justifyContent="center"
              userSelect="none"
              pointerEvents="none"
              zIndex={2}
              data-test="host-banner"
            >
              <HStack
                w="fit-content"
                color="livelinx.text"
                bgColor="livelinx.blue50"
                px={4}
                py={1}
                borderRadius="md"
              >
                <Text>
                  {t('home.slide.banner.host.multipleAnswers.title', {
                    amt:
                      slide.type === SlideType.QUESTION_MULTIPLE
                        ? slide.maxChoices
                        : slide.correctOrderIds.length,
                  })}
                </Text>
              </HStack>
            </Flex>
          </Fade>
        )}
      <Flex
        flexDir="column"
        h="100%"
        pt={isHost ? '0px' : '3%'} // time to choose notification
        pb={isHost ? '0px' : '7%'} // submit button
      >
        <ItemListContainer>
          {slide.items.map((item: QuestionSlideItemType, itemIndex: number) => {
            return (
              <ItemContainer key={itemIndex} slide={slide}>
                {item.type === SlideItemType.ANSWER_SELECTABLE && (
                  <SelectableAnswerItem
                    item={item}
                    isConfirmed={isAnswerConfirmed}
                    isSelected={currentSelectedAnswers.includes(item.id)}
                    answerIndex={
                      slide.type === SlideType.QUESTION_MULTIPLE_ORDERED
                        ? currentSelectedAnswers.indexOf(item.id)
                        : undefined
                    }
                    onMoreInfoCallback={() => {
                      if (item.assetRefs.length > 0) {
                        logHostAction(HostTrackingEventName.OPEN_ITEM_ASSET_LIST, {
                          itemId: item.id,
                          assetIds: item.assetRefs,
                        });
                        onShowAssets(item.assetRefs);
                      }
                    }}
                    onSelectCallback={() => onSelectableAnswerClick(item.id)}
                    isHost={isHost}
                    isGlowing={
                      !hasAtleastSelectedOneAnswer && !isAnswerConfirmed && glowingVoteItem === item
                    }
                    glowDuration={answerGlowDuration}
                    containsHostAsset={item.assetRefs.includes(currentHostAssetID ?? '')}
                  />
                )}
                {item.type === SlideItemType.ANSWER_TEXT && (
                  <TextAnswerItem
                    item={item}
                    isHost={isHost}
                    onTextChange={(text: string) => {
                      onTextAnswerChange([{ answerID: item.id, text }]);
                    }}
                  />
                )}
                {item.type === SlideItemType.INFO && (
                  <InfoItem
                    onMoreInfoCallback={() => {
                      if (item.assetRefs.length > 0) {
                        logHostAction(HostTrackingEventName.OPEN_ITEM_ASSET_LIST, {
                          itemId: null,
                          assetIds: item.assetRefs,
                        });
                        onShowAssets(item.assetRefs);
                      }
                    }}
                    item={item}
                    assetList={assetList}
                    containsHostAsset={item.assetRefs.includes(currentHostAssetID ?? '')}
                  />
                )}
              </ItemContainer>
            );
          })}
        </ItemListContainer>
        {!isHost &&
          !isAnswerConfirmed &&
          isSlideQuestionSlide(slide) &&
          slideHasAtLeastOneSelectableAnswer && (
            <Flex bottom="20px" position="absolute" w="full" justifyContent="center">
              <ConfirmButton
                isEnabled={isConfirmButtonEnabled}
                zIndex={10}
                isVisible={true}
                onConfirmCallback={() => onConfirmAnswer()}
                amountSelectedAnswers={currentSelectedAnswers.length}
                maxAnswers={(() => {
                  switch (slide.type) {
                    case SlideType.QUESTION_MULTIPLE:
                      return slide.maxChoices;
                    case SlideType.QUESTION_MULTIPLE_ORDERED:
                      return slide.correctOrderIds.length;
                    case SlideType.QUESTION_SINGLE:
                      return 1;
                    default:
                      captureSentry({
                        title: 'QuestionSlide maxAnswers unhandled case',
                        detail: {
                          slide: slide,
                        },
                      });
                      return 1;
                  }
                })()}
              />
            </Flex>
          )}
      </Flex>
    </>
  );
}

export default QuestionSlide;
